import { EyeIcon, PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { GQLDesignPageFragment, GQLProductFragment } from '@theprintbar/graphql';
import { Merch, TeamNameOption, cloneDeep, cn, getEmptyTeamName, getSortedSizes, getTeamNameState } from '@theprintbar/shared';
import { useCallback, useEffect, useState } from 'react';
import { ButtonSize, Input, Ping, Select, Table, TableBody, TableCell, TableRow, TransparentButton } from '../..';
import type { CreativeEditorInstance } from '../CreativeEditor';
import { editorTools } from '../CreativeEditor/editorTools';

interface TeamNameSizeProps {
	editor: CreativeEditorInstance;
	merch?: Merch;
	product: GQLProductFragment;
	updateMerch: (merch: Merch) => void;
	teamNameOptions?: Array<any>;
	content?: GQLDesignPageFragment;
}

export function TeamNamesSizes({ editor, merch, product, updateMerch, teamNameOptions, content }: TeamNameSizeProps) {
	const [teamNames, setTeamNames] = useState(1);
	const [open, setOpen] = useState(false);
	const [preview, setPreview] = useState(-1);
	const { quantity, complete } = getTeamNameState(merch);
	const allSizes = getSortedSizes(product);
	const firstStockedSize = allSizes.find(size => size.stocked) || allSizes[0];

	const handleChange = useCallback(
		(selectedIndex: number, option: TeamNameOption, value: string) => {
			const newMerch = cloneDeep(merch);
			if (!newMerch?.teamNames) return;
			const inputs = newMerch.teamNames.selected[selectedIndex].inputs;
			const input = inputs.find(i => i.id === option);
			if (input) input.name = value;

			setPreview(selectedIndex);
			editorTools.setTeamNameAssets(editor, newMerch.teamNames.selected[selectedIndex].inputs);

			updateMerch(newMerch);
		},
		[merch, updateMerch],
	);

	const handleSizeChange = useCallback(
		(selectedIndex: number, sizeCode: string, quantity: number) => {
			if (!merch) return;
			let newMerch = cloneDeep(merch);
			if (!newMerch?.teamNames) return;
			const index = newMerch.sizing.findIndex(({ size }) => size === sizeCode);

			if (index === -1) {
				if (!quantity) return;
				newMerch.sizing.push({ size: sizeCode, quantity });
			} else if (!!quantity) {
				newMerch.sizing[index].quantity = quantity;
			} else {
				newMerch.sizing.splice(index, 1);
			}

			newMerch.teamNames.selected[selectedIndex].quantity = quantity;
			updateMerch(newMerch);
		},
		[updateMerch, merch],
	);

	const increaseTeamNames = () => {
		const newMerch = cloneDeep(merch);

		if (!newMerch?.teamNames) return;
		newMerch.teamNames.selected.push(getEmptyTeamName(firstStockedSize.variant.size.code, newMerch.teamNames.available));
		newMerch.sizing.push({ size: firstStockedSize.variant.size.code, quantity: 1 });
		updateMerch(newMerch);
	};

	useEffect(() => {
		if (!editor?.engine.variable || !merch?.teamNames) return;
		if (preview > -1) {
			editorTools.setTeamNameAssets(editor, merch.teamNames.selected[preview].inputs);
			editor.engine.editor.addUndoStep();
		} else if (!!quantity) {
			const hasUpdated = editorTools.setTeamNameAssets(
				editor,
				merch.teamNames.available.map(option => ({
					id: option,
					name: teamNameOptions?.find(t => t.id === option)?.initialValue || '',
				})),
			);
			if (hasUpdated) {
				updateMerch(merch);
				editor.engine.editor.addUndoStep();
			}
		}
	}, [preview, editor, quantity]);

	useEffect(() => {
		if (!merch?.teamNames) return;
		const newMerch = cloneDeep(merch);
		let changed = false;

		if (merch?.teamNames && merch.teamNames.selected.length === 0) {
			const newMerch = cloneDeep(merch);
			newMerch.teamNames?.selected.push(getEmptyTeamName(firstStockedSize.variant.size.code, newMerch.teamNames.available));
			newMerch.sizing.push({ size: firstStockedSize.variant.size.code, quantity: 1 });
			updateMerch(newMerch);
		}

		// If we have existing team names, ensure all available options are included
		newMerch.teamNames?.selected.forEach(member => {
			merch.teamNames?.available.forEach(option => {
				if (!member.inputs.find(i => i.id === option)) {
					member.inputs.push({ id: option as TeamNameOption, name: '' });
					changed = true;
				}
			});
			// Remove inputs for fields that have been removed, but keep the value if it exists
			const filteredInputs = member.inputs.filter(i => merch.teamNames?.available.includes(i.id) || (i.name && i.name.trim() !== ''));
			if (filteredInputs.length !== member.inputs.length) {
				member.inputs = filteredInputs;
				changed = true;
			}
		});

		if (changed) {
			updateMerch(newMerch);
		}
	}, [merch?.teamNames?.available]);

	useEffect(() => {
		if (
			preview === -1 &&
			merch?.teamNames?.selected?.[0] &&
			merch.teamNames.selected[0].inputs.every(i => i.name && i.name.trim() !== '')
		) {
			setPreview(0);
		}
	}, [merch?.teamNames?.selected, preview]);

	return !!merch?.teamNames?.available.length ? (
		<>
			<div className="space-y-2.5">
				<div className="flex items-center space-x-4">
					<h5 className="text-xs uppercase">Team Names</h5>
					{!!quantity && (
						<button onClick={() => setOpen(true)} className="flex items-center gap-2 text-xs">
							<Ping colour={complete === quantity ? 'bg-green' : 'bg-orange'} />
							{complete} of {quantity} completed
						</button>
					)}
				</div>
			</div>
			<div>
				<Table className="pr-2">
					<TableBody>
						{merch.teamNames.selected.map((selected, sIndex) => {
							if (!selected) return null;
							return (
								<TableRow key={sIndex}>
									<TableCell className="border-none py-0 lg:px-0">
										<TransparentButton
											disabled={!selected.inputs.every(i => i.name && i.name.trim() !== '')}
											onClick={e => setPreview(sIndex)}
											className="px-0"
										>
											<EyeIcon className={cn('w-4 h-4', preview === sIndex ? 'text-blue' : 'text-grey')} />
										</TransparentButton>
									</TableCell>
									<TableCell className="w-24 border-none py-0 lg:px-1">
										<Select
											label="Size"
											name="size"
											outsideForm
											value={selected.size}
											onChange={e => {
												const newMerch = cloneDeep(merch);
												if (!newMerch?.teamNames) return;
												newMerch.teamNames.selected[sIndex].size = e.currentTarget.value;
												updateMerch(newMerch);
											}}
										>
											{allSizes.map(({ variant }) => {
												return (
													<option key={variant.id} value={variant.size.code}>
														{variant.size.code}
													</option>
												);
											})}
										</Select>
									</TableCell>
									<TableCell className="w-24 border-none bg-none py-0 lg:px-1">
										<Input
											outsideForm
											label="Qty"
											name="quantity"
											inputClassName="bg-none"
											placeholder="0"
											type="number"
											value={selected.quantity}
											onChange={e => handleSizeChange(sIndex, selected.size, Number(e.currentTarget.value))}
										/>
									</TableCell>
									{merch.teamNames?.available.map((teamOption, inputIndex) => {
										const input = selected.inputs.find(i => i.id === teamOption);
										const teamNameLabel = teamNameOptions?.find(t => t.id === teamOption)?.label || '';
										const savedValue = input?.name || '';
										return (
											<TableCell className="border-none py-0 lg:px-1">
												<Input
													outsideForm
													name={teamOption}
													inputClassName="bg-none"
													label={teamNameLabel}
													placeholder={teamNameOptions?.find(t => t.id === teamOption)?.initialValue}
													value={savedValue}
													onChange={e =>
														handleChange(sIndex, teamOption as TeamNameOption, e.currentTarget.value)
													}
												/>
											</TableCell>
										);
									})}
									<TableCell className="border-none py-0 lg:px-1">
										<TransparentButton
											className="px-0"
											onClick={() => {
												const newMerch = cloneDeep(merch);
												if (!newMerch?.teamNames) return;
												newMerch.teamNames.selected.splice(sIndex, 1);
												updateMerch(newMerch);

												// reset the preview
												if (preview === sIndex) {
													setPreview(-1);
												} else if (preview > sIndex) {
													setPreview(preview - 1);
												}
											}}
										>
											<TrashIcon className="text-red w-4 h-4" />
										</TransparentButton>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
				<TransparentButton
					className="flex-row-reverse items-center pl-0"
					size={ButtonSize.XS}
					icon={PlusCircleIcon}
					iconSize={ButtonSize.M}
					iconClass="ml-0 mr-1"
					onClick={() => increaseTeamNames()}
				>
					Add Team Member
				</TransparentButton>
			</div>
		</>
	) : null;
}
